import { Col, Row, Spin, Switch, Tooltip } from "antd-v5";
import ACComment from "components/ACComment";
import { getVocabulary } from "components/Vocabulary";
import { useActiveUserContext } from "contexts/ActiveUserContext";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { useGlobalUserListContext } from "contexts/GlobalUserListContext";
import useAPIResponse from "hooks/useAPIResponse";
import useModalRenderer from "hooks/useModalRenderer";
import _ from "lodash";
import { marked } from "marked";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import FindingAPI from "restAPI/FindingAPI";
import GlobalSettingAPI from "restAPI/GlobalSettingAPI";
import { CommentResponse } from "types/comment";

import ACLink from "ac-components/ACLink/ACLink";
import ACButton from "ac-components/ac-button/ACButton";
import ACCommentEditor, {
  ACCommentEditorProps,
  OnSumbitProps,
} from "ac-components/ac-comment-editor/ACCommentEditor";
import ExternalIcon from "ac-components/ac-icons/ExternalIcon";
import JiraIcon from "ac-components/ac-icons/JiraIcon";
import LoadingIcon from "ac-components/ac-icons/LoadingIcon";

import {
  FindingModalCommonConfig,
  convertCommentToNewFormat,
} from "../FindingDetails/common";
import FindingDetailsPopup from "./FindingDetailsPopup";

type CommentProps = CommentResponse & {
  ticketLink: string;
  ticketId: string;
  subProductVersion: string;
  isTicketComment: boolean;
  findingId: number;
};

export default function FindingComment({ finding }: { finding: any }) {
  const [comments, setComments] = useState<CommentProps[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAllTicketComments, setShowAllTicketComments] = useState(true);
  const [showCommentsFromAllVersions, setShowCommentsFromAllVersions] =
    useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const useOtherVersionFindingsRef = useRef<any[]>([]);
  const { getAllUsersList } = useGlobalUserListContext();
  const featureFlags = useFeatureFlag();

  const userList = useMemo(getAllUsersList, [getAllUsersList]);

  const [allowTicketComments, ticketCommentsStatusLoading] =
    useAPIResponse(() => {
      const promiseObj = GlobalSettingAPI.getCustomizedTicketSetting();
      promiseObj.promise = promiseObj.promise.then((data) => {
        return !_.isNil(data.allowTicketCommentsInFindings)
          ? data.allowTicketCommentsInFindings
          : false;
      });
      return promiseObj;
    }, []);

  const fetchComments = useCallback(
    (pageNum = 0, append = false) => {
      let allFindings = [finding];
      if (!_.isEmpty(useOtherVersionFindingsRef.current)) {
        allFindings = _.uniqBy(
          [...allFindings, ...useOtherVersionFindingsRef.current],
          "id",
        );
      }

      const promises: Promise<any>[] = [];
      const cancelPromises: (() => void)[] = [];
      allFindings.forEach(({ id }) => {
        const promiseObj = FindingAPI.getComments(id, {
          listTicketComments: showAllTicketComments && allowTicketComments,
          page: pageNum,
          size: 10,
        });
        promises.push(promiseObj.promise);
        cancelPromises.push(promiseObj.cancel);
      });

      const promise = Promise.all(promises).then((commentsResponse) => {
        const allComments = _.flatten(
          commentsResponse.map((response: { content: CommentResponse[] }, i) =>
            response.content.map((data: CommentResponse) => {
              const commentInput = convertCommentToNewFormat(data.comment);
              const commentOutput = marked.parse(commentInput) as string;
              return {
                ...data,
                subProductVersion: allFindings[i]?.subProductVersion,
                comment: commentOutput,
                findingId: allFindings[i]?.id,
              } as CommentProps;
            }),
          ),
        );

        // Update hasMore based on the first response (primary finding)
        const firstResponse = commentsResponse[0];

        if (append) {
          setComments((prevComments) =>
            _.uniqBy([...prevComments, ...allComments], "id"),
          );
          setHasMore(!firstResponse.last);
          setPage(pageNum);
          setLoadingMore(false);
        } else {
          setComments(allComments);
          setHasMore(!firstResponse.last);
          setPage(pageNum);
          setLoadingMore(false);
        }
      });
      return {
        promise,
        cancel: () => cancelPromises.forEach((cancel) => cancel()),
      };
    },
    [finding, showAllTicketComments, allowTicketComments],
  );

  const fetchNextversionBatch = useCallback(
    (page = 0, totalPages, cb = _.noop) => {
      const totalRequestsToBeMade = page === 0 ? 1 : totalPages - 1;
      const promises = [];
      for (let i = 0; i < totalRequestsToBeMade; i++) {
        promises.push(
          FindingAPI.getAll({
            page: page + i,
            size: 20,
            filters: {
              versionId: [finding.id],
            },
          }).promise,
        );
      }
      Promise.all(promises).then((responses) => {
        const allFindings = _.flatten(responses.map((r) => r.content));
        useOtherVersionFindingsRef.current = [
          ...useOtherVersionFindingsRef.current,
          ...allFindings,
        ];
        if (page === 0) {
          fetchNextversionBatch(page + 1, responses[0].totalPages, cb);
        } else {
          cb();
        }
      });
    },
    [finding],
  );

  const loadMoreComments = useCallback(() => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    const nextPage = page + 1;

    const promiseObj = fetchComments(nextPage, true);
    promiseObj.promise.catch((error) => {
      console.error("Error loading more comments:", error);
      setLoadingMore(false);
    });
  }, [page, hasMore, loadingMore, fetchComments]);

  useEffect(() => {
    if (!finding?.id || ticketCommentsStatusLoading) {
      return;
    }

    // Reset pagination state when filters change
    setPage(0);
    setHasMore(false);
    setLoadingMore(false);

    let promiseObj: { promise: Promise<any>; cancel: () => void };
    setLoading(true);
    if (featureFlags.VERSIONS && showCommentsFromAllVersions) {
      fetchNextversionBatch(0, undefined, () => {
        promiseObj = fetchComments(0, false);
        promiseObj.promise.finally(() => setLoading(false));
      });
    } else {
      useOtherVersionFindingsRef.current = [];
      promiseObj = fetchComments(0, false);
      promiseObj.promise.finally(() => setLoading(false));
    }
    return () => promiseObj?.cancel();
  }, [
    finding,
    featureFlags.VERSIONS,
    fetchNextversionBatch,
    fetchComments,
    ticketCommentsStatusLoading,
    showCommentsFromAllVersions,
    showAllTicketComments,
  ]);

  const onSend: ACCommentEditorProps["onSubmit"] = async ({
    htmlText: comment,
    markdownText,
    mentionUserIds,
  }) => {
    if (comment) {
      const payload = {
        htmlText: comment,
        userIds: mentionUserIds,
        markdownText,
        attachments: undefined,
      };
      const API = FindingAPI.postComment;
      let response = await API(finding.id, payload).promise;
      response = {
        ...response,
        findingId: finding.id,
        subProductVersion: finding.subProductVersion,
      };
      setComments((comments) => [...comments, response]);
    }
  };

  const onCommentUpdate = async (
    commentId: number,
    { htmlText: comment, markdownText, mentionUserIds }: OnSumbitProps,
  ) => {
    if (comment) {
      const payload = {
        htmlText: comment,
        userIds: mentionUserIds,
        markdownText,
        commentId,
        attachments: undefined,
      };
      const API = FindingAPI.editComment;
      const response = await API(finding.id, payload).promise;
      setComments((comments) => {
        const index = comments.findIndex(({ id }) => response.id === id);
        comments[index] = {
          ...response,
          findingId: finding.id,
          subProductVersion: finding.subProductVersion,
          isEdited: true,
        };
        return [...comments];
      });
    }
  };

  const renderList = (list: CommentProps[] | undefined) => {
    const disabledVersionedComments = !(
      featureFlags.VERSIONS &&
      (finding?.productVersion || finding?.subProductVersion)
    );
    return (
      <Spin spinning={loading}>
        <Row className="finding-comment">
          <Col span={24}>
            <ACCommentEditor onSubmit={onSend} users={userList} />
          </Col>
          <Col className="m-y-m" span={24}>
            {featureFlags.VERSIONS && (
              <div>
                <Switch
                  size="small"
                  className="m-r-xs"
                  onChange={setShowCommentsFromAllVersions}
                  checked={showCommentsFromAllVersions}
                  disabled={disabledVersionedComments}
                />{" "}
                Show comments from all versions
              </div>
            )}
            {allowTicketComments && (
              <div className="m-t-xs">
                <Switch
                  size="small"
                  checked={showAllTicketComments}
                  onChange={setShowAllTicketComments}
                  className="m-r-xs"
                />
                Show Ticket comments
              </div>
            )}
          </Col>
          {_.isEmpty(list) ? (
            <Col span={24}>
              <div className="text-center p-l">No comments</div>
            </Col>
          ) : (
            <>
              <Col span={24}>
                <ACComment
                  commentList={list}
                  onCommentSubmit={onCommentUpdate}
                  users={userList}
                  // @ts-expect-error
                  showEditButton={(item: CommentProps) => {
                    return (
                      item?.findingId &&
                      finding?.id &&
                      item.findingId === finding.id
                    );
                  }}
                  // @ts-expect-error
                  renderExtraMeta={(item: CommentProps) => (
                    <>
                      {showCommentsFromAllVersions && item.subProductVersion ? (
                        <Tooltip
                          getPopupContainer={(element) =>
                            element.parentElement!
                          }
                          title={`${getVocabulary("subProduct")} version`}
                        >
                          <span className="subproduct-version">
                            {item.findingId !== finding.id ? (
                              <VersionLink
                                versionNumber={item.subProductVersion}
                                findingId={item.findingId}
                              />
                            ) : (
                              item.subProductVersion
                            )}
                          </span>
                        </Tooltip>
                      ) : null}
                      {item.isTicketComment && (
                        <a
                          href={item.ticketLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="jira-link"
                        >
                          <ACButton size="sm" type="secondary" className="p-0">
                            <span className="d-flex align-items-center">
                              <JiraIcon className="jira-icon" />
                              {item.ticketId}
                              <ExternalIcon className="external-icon" />
                            </span>
                          </ACButton>
                        </a>
                      )}
                    </>
                  )}
                />
              </Col>
              {(loadingMore || hasMore) && (
                <Col span={24} className="text-center m-t-m">
                  {loadingMore ? (
                    <div
                      role="status"
                      aria-live="polite"
                      aria-label="Loading more comments"
                    >
                      <LoadingIcon className="font-size-24" />
                      <span className="sr-only">Loading more comments...</span>
                    </div>
                  ) : (
                    <ACLink
                      onClick={loadMoreComments}
                      role="button"
                      aria-label="Load more comments"
                      tabIndex={0}
                      onKeyPress={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          loadMoreComments();
                        }
                      }}
                    >
                      Load More
                    </ACLink>
                  )}
                </Col>
              )}
            </>
          )}
        </Row>
      </Spin>
    );
  };

  return <>{renderList(comments)}</>;
}

function VersionLink({
  findingId,
  versionNumber,
}: {
  versionNumber: string;
  findingId: number;
}) {
  const activeUser = useActiveUserContext();
  const modalRenderer = useModalRenderer();

  const showModal = modalRenderer.show;
  const { tenantId, businessUnitId } = activeUser;

  const onLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!e.metaKey && !e.altKey && !e.shiftKey) {
      e.preventDefault();
      showModal({
        content: <FindingDetailsPopup findingId={findingId} />,
        ...FindingModalCommonConfig,
      });
    }
  };

  return (
    <>
      <ACLink
        primary={false}
        overlay
        onClick={onLinkClick}
        href={`#/findings/${tenantId}/${businessUnitId}/${findingId}`}
      >
        {versionNumber}
      </ACLink>
      {modalRenderer.context}
    </>
  );
}
